import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';

import { type ChartConfig, ChartContainer } from '@/components/ui/chart';
import type { CallVolumeTrendPoint, TrendBucket } from '@/pages/analytics/types';
import { AXIS_LINE_STYLE, TICK_LINE_STYLE } from '@/pages/analytics/utils/chartAxis';
import { formatChartAxisDate, formatChartTooltipDate } from '@/pages/analytics/utils/chartDate';
import { COLOR_PRIMARY } from '@/pages/analytics/utils/chartPalette';
import { type PivotRow, scopeRows } from '@/pages/analytics/utils/series';

import { ChartEmptyState } from './ChartStates';
import { DotsLegend } from './DotsLegend';
import type { ChartScope } from './ScopeToggle';
import { StackedBarChart } from './StackedBarChart';

interface CallVolumeTrendChartProps {
  data: CallVolumeTrendPoint[];
  scope: ChartScope;
  trendBucket?: TrendBucket;
  height?: number;
}

interface Series {
  key: string;
  label: string;
}

// AQ-653: in Overall scope the single "Calls" bar is split into an eligible
// segment and an ineligible segment (stacked). These are the two synthetic
// series keys; the raw total is stashed on each row under `overall` for the
// tooltip. By-program scope is one stacked series per program (StackedBarChart).
const OVERALL_ELIGIBLE_KEY = 'overall_eligible';
const OVERALL_INELIGIBLE_KEY = 'overall_ineligible';

const buildOverallVolumeRows = (data: CallVolumeTrendPoint[]): PivotRow[] => {
  const dateMap = new Map<string, PivotRow>();
  for (const point of scopeRows(data, 'overall')) {
    const total = point.call_count ?? 0;
    const ineligible = point.ineligible_count ?? 0;
    // Guard against a negative eligible if counts ever disagree.
    const eligible = Math.max(total - ineligible, 0);
    dateMap.set(point.date, {
      date: point.date,
      overall: total,
      [OVERALL_ELIGIBLE_KEY]: eligible,
      [OVERALL_INELIGIBLE_KEY]: ineligible,
    });
  }
  return [...dateMap.values()].sort((a, b) => a.date.localeCompare(b.date));
};

const OVERALL_SERIES: Series[] = [
  { key: OVERALL_ELIGIBLE_KEY, label: 'Eligible' },
  { key: OVERALL_INELIGIBLE_KEY, label: 'Ineligible' },
];

// Thousands separators keep high-volume orgs readable (e.g. "34,058" not "34058").
const formatCountTick = (v: number) => v.toLocaleString('en-US');
const formatTooltipCount = (v: unknown) => (typeof v === 'number' ? v.toLocaleString('en-US') : '');

// Overall eligible uses the primary brand color; ineligible a light grey so it
// reads as a softer band on top. Module-level so it is a stable reference
// (no useMemo dependency churn).
const COLOR_INELIGIBLE = 'hsl(var(--muted-foreground) / 0.35)';
const colorFor = (key: string): string =>
  key === OVERALL_ELIGIBLE_KEY ? COLOR_PRIMARY : COLOR_INELIGIBLE;

// Overall tooltip: show Total, Eligible, and Ineligible for the hovered bucket.
// Recharts passes the stacked payload entries; we read the row values off the
// first entry's `payload`.
const OverallVolumeTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ payload?: PivotRow }>;
  label?: string | number;
  trendBucket: TrendBucket;
}> = ({ active, payload, label, trendBucket }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const total = typeof row.overall === 'number' ? row.overall : 0;
  const ineligible =
    typeof row[OVERALL_INELIGIBLE_KEY] === 'number' ? row[OVERALL_INELIGIBLE_KEY] : 0;
  const eligible = typeof row[OVERALL_ELIGIBLE_KEY] === 'number' ? row[OVERALL_ELIGIBLE_KEY] : 0;
  return (
    <div
      style={{
        background: 'hsl(var(--background))',
        border: '1px solid hsl(var(--border))',
        borderRadius: 6,
        fontSize: 12,
        padding: '8px 10px',
        zIndex: 20,
      }}
    >
      <div className="mb-1 font-medium text-foreground">
        {formatChartTooltipDate(label, trendBucket)}
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">Total calls</span>
        <span className="font-medium text-foreground">{formatTooltipCount(total)}</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">Eligible</span>
        <span className="text-foreground">{formatTooltipCount(eligible)}</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">Ineligible</span>
        <span className="text-foreground">{formatTooltipCount(ineligible)}</span>
      </div>
    </div>
  );
};

const OverallCallVolumeChart: React.FC<Omit<CallVolumeTrendChartProps, 'scope'>> = ({
  data,
  trendBucket = 'day',
  height = 220,
}) => {
  const rows = useMemo(() => buildOverallVolumeRows(data), [data]);

  const config: ChartConfig = useMemo(() => {
    const c: ChartConfig = {};
    OVERALL_SERIES.forEach(s => {
      c[s.key] = { label: s.label, color: colorFor(s.key) };
    });
    return c;
  }, []);

  if (!rows.length) {
    return <ChartEmptyState height={height} />;
  }

  return (
    <ChartContainer
      config={config}
      className="!aspect-auto w-full"
      style={{ height }}
      role="img"
      aria-label={`Call volume bar chart, overall across ${rows.length} points`}
    >
      <BarChart data={rows} margin={{ top: 10, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={value => formatChartAxisDate(value, trendBucket)}
          tickLine={TICK_LINE_STYLE}
          axisLine={AXIS_LINE_STYLE}
          fontSize={11}
        />
        <YAxis
          allowDecimals={false}
          tickFormatter={formatCountTick}
          tickLine={TICK_LINE_STYLE}
          axisLine={AXIS_LINE_STYLE}
          fontSize={11}
          width={36}
        />
        <Tooltip
          wrapperStyle={{ zIndex: 20 }}
          cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
          content={<OverallVolumeTooltip trendBucket={trendBucket} />}
        />

        {OVERALL_SERIES.map((s, index) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label}
            stackId="volume"
            fill={colorFor(s.key)}
            // Round only the top segment of each stack: the last series drawn is
            // the top of the stack in recharts.
            radius={index === OVERALL_SERIES.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
            animationDuration={500}
          />
        ))}

        <Legend content={<DotsLegend />} verticalAlign="bottom" wrapperStyle={{ paddingTop: 4 }} />
      </BarChart>
    </ChartContainer>
  );
};

export const CallVolumeTrendChart: React.FC<CallVolumeTrendChartProps> = ({
  data,
  scope,
  trendBucket = 'day',
  height = 220,
}) => {
  const byProgramRows = useMemo(
    () =>
      scopeRows(data, 'by_program').map(p => ({
        x: p.date,
        group: p.group,
        group_name: p.group_name,
        value: p.call_count,
      })),
    [data],
  );

  if (scope === 'overall') {
    return <OverallCallVolumeChart data={data} trendBucket={trendBucket} height={height} />;
  }

  return (
    <StackedBarChart
      rows={byProgramRows}
      xFormatter={x => formatChartAxisDate(x, trendBucket)}
      tooltipXFormatter={x => formatChartTooltipDate(x, trendBucket)}
      // Per-program bars are total calls for that program — spell it out so the by-program
      // tooltip isn't confused with the eligible/ineligible split shown in the overall view.
      valueFormatter={v => `${v.toLocaleString('en-US')} total calls`}
      ariaLabelPrefix="Call volume bar chart"
      seriesNoun="program"
      height={height}
    />
  );
};
